import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { useAddToast } from '../../contexts/ToastContext';
import { useLocalStorage } from '../../helpers/localStorage';
import usePollJobs from '../../hooks/api/jobs/usePollJobs';
import { openInNewTab } from '../../utils';
import type { NewToast } from '../Toaster/types';

import type {
  JobCallbacks,
  JobNotification,
  JobsStore,
  JobToken,
} from './types';

const JOBS_LOCAL_STORAGE_KEY = '__FOSSA__jobs';

//  Used for stored jobs whose notification is missing or malformed, e.g. entries
//  queued by an older client that stored only the job name.
const GENERIC_NOTIFICATION: JobNotification = {
  success: { content: 'Your job finished.' },
  failure: { content: 'Your job failed.' },
};

//  Links are opened on click, whether a caller passed them in this session or they were
//  restored from localStorage, so allow only explicit http(s) URLs and paths that still
//  resolve to this origin after the browser normalises them. URL parsing
//  strips tabs and newlines and treats `\` as `/`, so a prefix check alone would let
//  `/\host` or `/\n/host` through as protocol-relative links.
function isSafeHref(href: string): boolean {
  if (/^https?:\/\//i.test(href)) {
    return true;
  }
  if (!href.startsWith('/')) {
    return false;
  }
  try {
    return new URL(href, window.location.origin).origin === window.location.origin;
  } catch {
    //  An unparseable path (e.g. `//[`) is not a link we can safely open.
    return false;
  }
}

const jobNotificationSchema: z.ZodType<JobNotification> = z.object({
  success: z.object({
    content: z.string(),
    persistent: z.boolean().optional(),
    link: z.object({ text: z.string(), href: z.string() }).optional(),
  }),
  failure: z.object({ content: z.string() }),
});

const storedJobSchema = z.object({ notification: jobNotificationSchema });

const storedJobsSchema = z.record(z.string(), z.unknown());

//  `/api/jobs/status` rejects the whole poll if any token is not a GUID.
const jobTokenSchema = z.guid();

/**
 * localStorage may hold anything, including jobs written by an older client, so
 * every entry is validated. Entries whose token is not a GUID are dropped, since
 * one would make every poll fail. Entries without a valid notification are kept
 * (so they are still polled) and fall back to generic toast text.
 */
function parseStoredJobs(stored: unknown): JobsStore {
  const parsed = storedJobsSchema.safeParse(stored);
  if (!parsed.success) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(parsed.data)
      .filter(([token]) => jobTokenSchema.safeParse(token).success)
      .map(([token, job]) => {
        const storedJob = storedJobSchema.safeParse(job);
        return [token, { notification: storedJob.success ? storedJob.data.notification : GENERIC_NOTIFICATION }];
      }),
  );
}

function buildSuccessToast({ content, persistent, link }: JobNotification['success']): NewToast {
  const toast = {
    type: 'success' as const,
    content,
    ...(persistent ? { persistent } : {}),
  };
  if (!link) {
    return toast;
  }
  if (!isSafeHref(link.href)) {
    //  oxlint-disable-next-line no-console -- the frontend has no logger, and this flags a caller passing a link we refuse to open
    console.warn(`JobNotificationHub: dropped the toast link with unsafe href ${JSON.stringify(link.href)}`);
    return toast;
  }
  return { ...toast, action: { text: link.text, onAction: () => openInNewTab(link.href) } };
}

/**
 * Hook which powers the JobNotificationProvider. This hook stores a queue of jobs
 * which the user has requested to be notified about. If there are jobs in the queue,
 * it will poll the API for updates on the job status. If any of the returned jobs are
 * marked as either finished or failed, a toast will be displayed and those jobs will
 * be removed from the queue. Note that it does not poll if there are no jobs in the queue.
 * This state is backed up to localStorage, so that jobs will still be polled for if the user
 * reloads or closes the page.
 *
 * Each job carries the notification its caller supplied, so the toast text (and an optional
 * link) describes why the job was queued rather than what kind of job it is.
 *
 * Returns the onAddJob function, as that it all the Provider needs to expose to the app,
 * because all the state and logic is handled here.
 *
 * DO NOT use this directly (it should only be used by the Provider since it is stateful).
 * Instead use the useAddJobToNotificationQueue hook, which provides the onAddJob function.
 *
 * @returns {AddJobFn} onAddJob - A function which takes the jobToken and its notification and adds it to the queue.
 */
const useJobNotificationHub = () => {
  const ls = useLocalStorage<JobsStore>(JOBS_LOCAL_STORAGE_KEY);
  const addToast = useAddToast();
  const [jobs, setJobs] = useState<JobsStore>(() => parseStoredJobs(ls.get()));
  //  Per-job completion callbacks, keyed by token. Kept in a ref (not state or
  //  localStorage) because functions can't be serialised, and a caller's
  //  callback should fire exactly once for the job it registered.
  const callbacksRef = useRef<Record<string, JobCallbacks>>({});

  //  Derive the list of tokens, which are used as the keys for the jobs object.
  const jobTokens = Object.keys(jobs);
  //  Only enable polling if there are jobs to poll for. React Query will not fetch if enabled is false.
  const shouldFetch = jobTokens.length > 0;
  //  Fetch the jobs by their tokens.
  const { data: fetchedJobs, isLoading, isError } = usePollJobs(jobTokens, shouldFetch);

  const onAddSuccessToast = useCallback((jobToken: JobToken) => {
    const notification = jobs[jobToken]?.notification ?? GENERIC_NOTIFICATION;
    addToast(buildSuccessToast(notification.success));
  }, [addToast, jobs]);

  const onAddFailureToast = useCallback((jobToken: JobToken) => {
    const notification = jobs[jobToken]?.notification ?? GENERIC_NOTIFICATION;
    addToast({ type: 'error', content: notification.failure.content });
  }, [addToast, jobs]);

  //  Effect where we check the if any of the returned jobs are in our queue.
  //  If they are, we check if they are finished or failed and display a toast.
  //  After they are toasted, we remove them from the queue.
  useEffect(() => {
    //  "Any time the fetched jobs data changes and the data is not loading"
    if (fetchedJobs && !isError && !isLoading) {
      const existingJobs = new Set(jobTokens);
      //  List of job tokens to remove.
      const jobsToRemove = new Set();

      //  Update the user with a toast if their job has finished or failed.
      Object.entries(fetchedJobs.jobs).forEach(([token, job]) => {
        if (existingJobs.has(token)) {
          if (job.status === 'finished') {
            onAddSuccessToast(token);
            callbacksRef.current[token]?.onFinished?.();
            jobsToRemove.add(token);
          } else if (job.status === 'failed') {
            onAddFailureToast(token);
            callbacksRef.current[token]?.onFailed?.();
            jobsToRemove.add(token);
          }
        }
      });

      //  If any jobTokens are missing from the response, remove those from the store as well.
      //  This is mostly for developer sanity, as it can happen if you are connected to a different environment (like staging),
      //  and then switch back to your local environment.
      const fetchedJobsTokens = new Set(Object.keys(fetchedJobs.jobs));
      const missingJobs = jobTokens.filter((token) => !fetchedJobsTokens.has(token));
      //  Add them to the list of jobs to remove.
      missingJobs.forEach((token) => jobsToRemove.add(token));

      //  If we have any jobs which need to be removed from the queue, do so.
      if (jobsToRemove.size > 0) {
        //  Drop their callbacks too so they can't leak or fire twice.
        jobsToRemove.forEach((token) => {
          delete callbacksRef.current[token as string];
        });
        setJobs((prevJobs) => {
          const newJobs = Object.fromEntries(
            Object.entries(prevJobs)
              .filter(([token]) => !jobsToRemove.has(token)),
          );
          //  Set the job in localStorage
          ls.set(newJobs);
          return newJobs;
        });
      }
    }
  }, [ls, fetchedJobs, isError, isLoading, jobTokens, onAddSuccessToast, onAddFailureToast]);

  /**
   * Adds a new job to the queue be polled for. This also sets it in localStorage,
   * so that if the user was to reload the page, the job would still be in the queue.
   *
   * Optionally takes per-job callbacks fired when polling sees the job finish or
   * fail. These live in memory only, so they are not restored on reload.
   *
   * A success `link.href` must be an http(s) URL or a same-origin root-relative path
   * (e.g. `/api/reports/download/abc`); any other link is dropped from the toast.
   */
  const onAddJob = useCallback((jobToken: JobToken, notification: JobNotification, callbacks?: JobCallbacks) => {
    if (callbacks) {
      callbacksRef.current[jobToken] = callbacks;
    }
    setJobs((prevJobs) => {
      const newJobs: JobsStore = {
        ...prevJobs,
        [jobToken]: { notification },
      };
      //  Store the jobs in localStorage
      ls.set(newJobs);
      //  Set the state here
      return newJobs;
    });
  }, [ls]);

  return onAddJob;
};

export default useJobNotificationHub;
